import React from 'react'
import { Link } from 'react-router-dom'
import Typography from 'material-ui/Typography'
import Grid from 'material-ui/Grid'
import TextField from 'material-ui/TextField'
import Button from 'material-ui/Button'
import Select from 'material-ui/Select'
import Input, { InputLabel } from 'material-ui/Input'
import { FormControl, FormControlLabel, FormHelperText } from 'material-ui/Form'
import { MenuItem } from 'material-ui/Menu'
import { ListSubheader } from 'material-ui/List'
import Checkbox from 'material-ui/Checkbox'
import { withStyles } from 'material-ui/styles'
import { ArrowLeft, ArrowRight, CircleCheck } from 'lucide-react'
import styles from './styles'
import TermsAndCondition from './termsAndCondition'
import { getCountryDisplayName, getCountryGroups } from '../../common/country-options'
import {
  getChartLineStyleOptions,
  getChartMeasurementOptions,
  getEmployeeRangeOptions,
  getEquipmentUseLabel,
  getEquipmentUseOptions,
  getLanguageOptions,
  getOrganisationActivityLabel,
  getOrganisationActivityOptions,
  getWiremapDisplayPreferenceOptions
} from '../../common/organisation-preferences'

const STEPS = [
  { titleKey: 'signup_wizard_account', title: 'Your account', descriptionKey: 'signup_wizard_account_description', description: 'Contact and sign-in details' },
  { titleKey: 'signup_wizard_organisation', title: 'Organisation', descriptionKey: 'signup_wizard_organisation_description', description: 'Company and report preferences' },
  { titleKey: 'signup_wizard_work', title: 'Your work', descriptionKey: 'signup_wizard_work_description', description: 'How your team uses TREND equipment' },
  { titleKey: 'signup_wizard_review', title: 'Review', descriptionKey: 'signup_wizard_review_description', description: 'Confirm the details entered' },
  { titleKey: 'signup_wizard_verify', title: 'Verify account', descriptionKey: 'signup_wizard_verify_description', description: 'Confirm your contact details' }
]

const initialInfo = {
  firstname: '',
  lastname: '',
  enabled: true,
  emailaddress: '',
  contactnumber: '',
  password: '',
  confirmpassword: '',
  companyname: '',
  country: '',
  address: '',
  city: '',
  state: '',
  zipcode: '',
  language: 'en-GB',
  chartmeasurement: 'Meters',
  chartlinestyle: 'Standard',
  wiremappreference: 'Standard',
  organisationactivities: [],
  otheractivity: '',
  employeerange: '',
  equipmentuse: [],
  equipmentuseother: '',
  testingapplications: '',
  termsandconditions: false,
  verificationcode: ''
}

class SignupForm extends React.Component {
  state = {
    info: { ...initialInfo },
    errors: {},
    step: 0,
    submitted: false,
    open: false,
    verificationCodeSent: false,
    verificationError: '',
    otpVerified: false,
    verifyingOtp: false,
    submitting: false
  }

  translate = (key, fallback) => {
    const { translate } = this.props
    const interpolationData = fallback && typeof fallback === 'object' ? fallback : undefined
    const translated = translate ? translate(key, interpolationData) : null

    if (translated === key && typeof fallback === 'string') {
      return fallback
    }

    return translated || fallback
  }

  getSignUpErrorMessage = error => {
    const message = error && error.message ? error.message : error

    if (message === 'error_duplicateuser') {
      return this.translate('error_duplicateuser', 'An account already exists for this email address.')
    }

    return message || this.translate('signup_error_create_account', 'Unable to create your account. Please review your details and try again.')
  }

  handleChange = name => event => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value

    if ((name === 'emailaddress' || name === 'contactnumber') && (this.props.mfaVerified || this.state.otpVerified)) {
      this.props.resetMfaState()
    }

    this.setState(previousState => ({
      info: {
        ...previousState.info,
        [name]: value
      },
      errors: {
        ...previousState.errors,
        [name]: null
      },
      otpVerified: name === 'emailaddress' || name === 'contactnumber' ? false : previousState.otpVerified
    }))
  }

  handleMultiSelectChange = (name, value) => event => {
    const isChecked = event.target.checked

    this.setState(previousState => {
      const selectedValues = previousState.info[name]
      const nextValues = isChecked
        ? [...selectedValues, value]
        : selectedValues.filter(selectedValue => selectedValue !== value)

      return {
        info: {
          ...previousState.info,
          [name]: nextValues
        },
        errors: {
          ...previousState.errors,
          [name]: null
        }
      }
    })
  }

  validateStep = step => {
    const { info } = this.state
    const errors = {}
    const requireValue = (name, message = this.translate('formfield_error_general_required', 'This field is required.')) => {
      if (!info[name] || !String(info[name]).trim()) {
        errors[name] = message
      }
    }

    if (step === 0) {
      requireValue('firstname')
      requireValue('lastname')
      requireValue('contactnumber')
      requireValue('emailaddress')
      requireValue('password')
      requireValue('confirmpassword')

      if (info.emailaddress && !/^\S+@\S+\.\S+$/.test(info.emailaddress)) {
        errors.emailaddress = this.translate('signup_error_email_invalid', 'Enter a valid email address.')
      }

      if (info.password && info.confirmpassword && info.password !== info.confirmpassword) {
        errors.password = this.translate('signup_error_password_mismatch', 'Passwords do not match.')
        errors.confirmpassword = this.translate('signup_error_password_mismatch', 'Passwords do not match.')
      }

      if (info.password && !/^(?=.*?[A-Z])(?=.*?[a-z])(?=.*?[0-9]).{8,}$/.test(info.password)) {
        errors.password = this.translate('signup_error_password_strength', 'Use at least 8 characters, including upper-case, lower-case and a number.')
      }
    }

    if (step === 1) {
      ['companyname', 'country', 'address', 'city', 'state', 'zipcode', 'language', 'chartmeasurement', 'chartlinestyle', 'wiremappreference']
        .forEach(name => requireValue(name))
    }

    if (step === 2) {
      if (!info.organisationactivities.length) {
        errors.organisationactivities = this.translate('organisation_preferences_activity_required', 'Select at least one activity.')
      }
      if (info.organisationactivities.includes('Other (please specify)') && !info.otheractivity.trim()) {
        errors.otheractivity = this.translate('organisation_preferences_other_activity_required', 'Describe the other activity.')
      }
      requireValue('employeerange', this.translate('signup_error_field_team_required', 'Select the size of your field team.'))
      if (!info.equipmentuse.length) {
        errors.equipmentuse = this.translate('organisation_preferences_equipment_required', 'Select at least one option.')
      }
      if (info.equipmentuse.includes('Other') && !info.equipmentuseother.trim()) {
        errors.equipmentuseother = this.translate('organisation_preferences_other_equipment_required', 'Describe the other equipment use.')
      }
      requireValue('testingapplications', this.translate('signup_error_testing_applications_required', 'Describe the applications your team will test.'))
    }

    if (step === 3 && !info.termsandconditions) {
      errors.termsandconditions = this.translate('signup_error_terms_required', 'Accept the terms and conditions to continue.')
    }

    if (step === 4) {
      requireValue('verificationcode', this.translate('signup_error_verification_code_required', 'Enter the verification code.'))
      if (info.verificationcode && !/^\d{6}$/.test(info.verificationcode)) {
        errors.verificationcode = this.translate('signup_error_verification_code_format', 'Enter the 6-digit verification code.')
      }
    }

    this.setState({ errors })
    return Object.keys(errors).length === 0
  }

  goToNextStep = () => {
    const { step } = this.state
    if (this.validateStep(step)) {
      if (step === 3) {
        this.props.requestOtpVerification({
          emailAddress: this.state.info.emailaddress,
          contactNumber: this.state.info.contactnumber
        })
      }
      this.setState({ step: Math.min(step + 1, STEPS.length - 1), verificationError: '' })
    }
  }

  goToPreviousStep = () => {
    this.setState(previousState => ({
      step: Math.max(previousState.step - 1, 0),
      errors: {}
    }))
  }

  handleSubmit = event => {
    event.preventDefault()
    const { step, verificationCodeSent } = this.state

    if (step < STEPS.length - 1) {
      this.goToNextStep()
      return
    }

    if (verificationCodeSent && this.validateStep(step)) {
      this.completeSignUp()
    }
  }

  startAgain = () => {
    this.setState({
      info: { ...initialInfo },
      errors: {},
      step: 0,
      submitted: false,
      verificationCodeSent: false,
      verificationError: '',
      otpVerified: false,
      verifyingOtp: false,
      submitting: false
    })
    this.props.resetMfaState()
  }

  sendVerificationCode = () => {
    this.setState({ verificationError: '' })
    return this.props.sendOtp({ method: 'email' })
      .then(() => this.setState({ verificationCodeSent: true }))
      .catch(() => this.setState({ verificationError: this.translate('signup_error_send_verification_code', 'Unable to send a verification code. Please try again.') }))
  }

  completeSignUp = () => {
    const { info } = this.state

    if (!this.state.verificationCodeSent) {
      return
    }

    if (!this.state.otpVerified) {
      this.setState({ verificationError: '', verifyingOtp: true })
      this.props.verifyOtp({ otp: info.verificationcode })
        .then(() => this.setState({ otpVerified: true, verifyingOtp: false }, this.completeSignUp))
        .catch(() => this.setState({
          verifyingOtp: false,
          verificationError: this.translate('signup_error_verification_invalid', 'The verification code is invalid or has expired.')
        }))
      return
    }

    this.setState({ submitting: true, verificationError: '' })
    const {
      confirmpassword,
      termsandconditions,
      verificationcode,
      otheractivity,
      employeerange,
      equipmentuse,
      equipmentuseother,
      wiremappreference,
      ...signUpInfo
    } = info

    this.props.signup({
      ...signUpInfo,
      wiremapdisplaypreference: wiremappreference,
      organisationactivityother: otheractivity,
      fieldtechnicianrange: employeerange,
      equipmentusage: equipmentuse,
      equipmentusageother: equipmentuseother
    })
      .then(result => {
        if (!result || result.navigated) {
          return
        }

        this.setState({ submitted: true, submitting: false })
      })
      .catch(error => this.setState({
        submitting: false,
        verificationError: this.getSignUpErrorMessage(error)
      }))
  }

  openSignupModal = () => {
    this.setState({ open: true })
  }

  closeSignupModal = () => {
    this.setState({ open: false })
  }

  renderInput = (name, label, options = {}) => {
    const { classes } = this.props
    const { errors, info } = this.state

    const field = (
      <TextField
        className={classes.textField}
        error={Boolean(errors[name])}
        fullWidth
        helperText={errors[name] || options.helperText}
        id={name}
        label={options.stackedLabel ? undefined : label}
        multiline={options.multiline}
        required={options.required !== false}
        rows={options.rows}
        type={options.type || 'text'}
        inputProps={options.inputProps}
        value={info[name]}
        onChange={this.handleChange(name)}
      />
    )

    return options.stackedLabel ? (
      <React.Fragment>
        <label className={classes.questionLabel} htmlFor={name}>
          {label} {options.required !== false && <span className={classes.required}>*</span>}
        </label>
        {field}
      </React.Fragment>
    ) : field
  }

  renderSelect = (name, label, options, stackedLabel = false) => {
    const { classes } = this.props
    const { errors, info } = this.state

    return (
      <FormControl className={classes.formControl} error={Boolean(errors[name])} required>
        {stackedLabel ? (
          <label className={classes.questionLabel} htmlFor={name}>
            {label} <span className={classes.required}>*</span>
          </label>
        ) : (
          <InputLabel htmlFor={name}>{label}</InputLabel>
        )}
        <Select
          input={<Input id={name} name={name} />}
          value={info[name]}
          onChange={this.handleChange(name)}
        >
          {options.map(option => {
            const value = option.value || option
            return (
              <MenuItem key={value} value={value}>
                {option.label || option}
              </MenuItem>
            )
          })}
        </Select>
        {errors[name] && <FormHelperText>{errors[name]}</FormHelperText>}
      </FormControl>
    )
  }

  renderCountrySelect = () => {
    const { activeLanguage, classes } = this.props
    const { errors, info } = this.state
    const { priority, allOtherCountries } = getCountryGroups(activeLanguage)

    return (
      <FormControl className={classes.formControl} error={Boolean(errors.country)} required>
        <InputLabel htmlFor="country">{this.translate('country', 'Country')}</InputLabel>
        <Select
          input={<Input id="country" name="country" />}
          value={info.country}
          onChange={this.handleChange('country')}
        >
          <ListSubheader className={classes.countryGroupHeader} component="div" disableSticky>
            {this.translate('signup_country_priority_group', 'United Kingdom, United States and Europe')}
          </ListSubheader>
          {priority.map(country => (
            <MenuItem key={country.code} value={country.code}>{country.name}</MenuItem>
          ))}
          <ListSubheader className={classes.countryGroupHeader} component="div" disableSticky>
            {this.translate('signup_country_all_group', 'All other countries')}
          </ListSubheader>
          {allOtherCountries.map(country => (
            <MenuItem key={country.code} value={country.code}>{country.name}</MenuItem>
          ))}
        </Select>
        {errors.country && <FormHelperText>{errors.country}</FormHelperText>}
      </FormControl>
    )
  }

  renderMultiSelect = (name, label, options, otherName) => {
    const { classes } = this.props
    const { errors, info } = this.state
    const selectedValues = info[name]
    const hasOther = selectedValues.includes('Other') || selectedValues.includes('Other (please specify)')

    return (
      <div className={classes.questionGroup}>
        <Typography className={classes.questionLabel} component="p">
          {label} <span className={classes.required}>*</span>
        </Typography>
        <div className={classes.checkboxList}>
          {options.map(option => {
            const value = option.value || option
            const optionLabel = option.label || option
            return (
              <FormControlLabel
                key={value}
                className={classes.checkboxControl}
                control={
                  <Checkbox
                    checked={selectedValues.includes(value)}
                    color="primary"
                    onChange={this.handleMultiSelectChange(name, value)}
                  />
                }
                label={optionLabel}
              />
            )
          })}
        </div>
        {errors[name] && <FormHelperText error>{errors[name]}</FormHelperText>}
        {hasOther && this.renderInput(otherName, this.translate('signup_please_specify', 'Please specify'), { required: true })}
      </div>
    )
  }

  renderAccountStep = () => {
    const { classes } = this.props

    return (
      <Grid container spacing={16}>
        <Grid item xs={12} sm={6}>{this.renderInput('firstname', this.translate('signup_firstname', 'First name'))}</Grid>
        <Grid item xs={12} sm={6}>{this.renderInput('lastname', this.translate('signup_lastname', 'Last name'))}</Grid>
        <Grid item xs={12} sm={6}>{this.renderInput('emailaddress', this.translate('signup_work_email', 'Work email address'), { type: 'email' })}</Grid>
        <Grid item xs={12} sm={6}>{this.renderInput('contactnumber', this.translate('signup_contactnumber', 'Contact number'), { type: 'tel' })}</Grid>
        <Grid item xs={12} sm={6}>{this.renderInput('password', this.translate('signup_password', 'Create password'), { type: 'password', helperText: this.translate('signup_password_help', 'At least 8 characters, including upper-case, lower-case and a number.') })}</Grid>
        <Grid item xs={12} sm={6}>{this.renderInput('confirmpassword', this.translate('signup_confirmpassword', 'Confirm password'), { type: 'password' })}</Grid>
        <Grid item xs={12} className={classes.stepNote}>
          {this.translate('signup_account_note', 'Your contact details will be used for account verification and service communications.')}
        </Grid>
      </Grid>
    )
  }

  renderOrganisationStep = () => {
    const { activeLanguage, languages } = this.props
    const languageOptions = getLanguageOptions(languages, activeLanguage)

    return (
      <Grid container spacing={16}>
        <Grid item xs={12}>{this.renderInput('companyname', this.translate('signup_companyname', 'Organisation name'))}</Grid>
        <Grid item xs={12} sm={6}>{this.renderCountrySelect()}</Grid>
        <Grid item xs={12} sm={6}>{this.renderInput('address', this.translate('signup_building_name', 'Building number / name'))}</Grid>
        <Grid item xs={12} sm={4}>{this.renderInput('city', this.translate('city', 'City'))}</Grid>
        <Grid item xs={12} sm={4}>{this.renderInput('state', this.translate('signup_state_county', 'State / county'))}</Grid>
        <Grid item xs={12} sm={4}>{this.renderInput('zipcode', this.translate('subscription_wizard_postal_code', 'Postal code'))}</Grid>
        <Grid item xs={12} className={this.props.classes.sectionDivider}>
          <Typography className={this.props.classes.sectionTitle} component="h3">{this.translate('organisation_report_preferences', 'Report preferences')}</Typography>
          <Typography className={this.props.classes.sectionDescription}>{this.translate('signup_report_preferences_note', 'These can be changed later from your organisation settings.')}</Typography>
        </Grid>
        <Grid item xs={12} sm={6}>{this.renderSelect('language', this.translate('language', 'Language'), languageOptions)}</Grid>
        <Grid item xs={12} sm={6}>{this.renderSelect('chartmeasurement', this.translate('organisation_report_measurement', 'Report measurement unit'), getChartMeasurementOptions(this.translate))}</Grid>
        <Grid item xs={12} sm={6}>{this.renderSelect('chartlinestyle', this.translate('organisation_report_chart_style', 'Report chart line style'), getChartLineStyleOptions(this.translate))}</Grid>
        <Grid item xs={12} sm={6}>{this.renderSelect('wiremappreference', this.translate('organisation_wiremap_preference', 'LanTEK wiremap display preference'), getWiremapDisplayPreferenceOptions(this.translate))}</Grid>
      </Grid>
    )
  }

  renderWorkStep = () => (
    <div>
      {this.renderMultiSelect(
        'organisationactivities',
        this.translate('signup_activities_question', "Which of the following best describes your organisation's activities? (Select all that apply.)"),
        getOrganisationActivityOptions(this.translate),
        'otheractivity'
      )}
      <div className={this.props.classes.questionGroup}>
        {this.renderSelect('employeerange', this.translate('signup_field_team_question', 'How many field technicians or engineers does your organisation employ?'), getEmployeeRangeOptions(this.translate), true)}
      </div>
      {this.renderMultiSelect(
        'equipmentuse',
        this.translate('signup_equipment_use_question', 'How will your TREND Network equipment be used? (Select all that apply.)'),
        getEquipmentUseOptions(this.translate),
        'equipmentuseother'
      )}
      <div className={this.props.classes.questionGroup}>
        {this.renderInput(
          'testingapplications',
          this.translate('signup_testing_applications_question', 'What type of application will you be testing with your TREND Network equipment?'),
          { multiline: true, rows: 2, stackedLabel: true, helperText: this.translate('signup_testing_applications_help', 'For example, structured cabling, fibre backbones or active network links.') }
        )}
      </div>
    </div>
  )

  renderReviewStep = () => {
    const { activeLanguage, classes } = this.props
    const { errors, info } = this.state

    return (
      <div>
        <div className={classes.reviewGrid}>
          <div className={classes.reviewSection}>
            <Typography className={classes.reviewHeading}>{this.translate('signup_review_account', 'Account')}</Typography>
            <Typography>{info.firstname} {info.lastname}</Typography>
            <Typography>{info.emailaddress}</Typography>
            <Typography>{info.contactnumber}</Typography>
          </div>
          <div className={classes.reviewSection}>
            <Typography className={classes.reviewHeading}>{this.translate('signup_review_organisation', 'Organisation')}</Typography>
            <Typography>{info.companyname}</Typography>
            <Typography>{info.address}, {info.city}</Typography>
            <Typography>{info.state}, {info.zipcode}, {getCountryDisplayName({ code: info.country, name: info.country }, activeLanguage)}</Typography>
          </div>
          <div className={classes.reviewSection}>
            <Typography className={classes.reviewHeading}>{this.translate('signup_review_organisation_profile', 'Organisation profile')}</Typography>
            <Typography>{info.organisationactivities.map(value => getOrganisationActivityLabel(value, this.translate)).join(', ')}</Typography>
            <Typography>{this.translate('signup_review_field_team', { count: info.employeerange })}</Typography>
            <Typography>{info.equipmentuse.map(value => getEquipmentUseLabel(value, this.translate)).join(', ')}</Typography>
          </div>
        </div>
        <div className={classes.termsRow}>
          <FormControlLabel
            className={classes.termsControl}
            control={
              <Checkbox
                checked={info.termsandconditions}
                color="primary"
                onChange={this.handleChange('termsandconditions')}
              />
            }
            label=""
          />
          <Typography component="span">
            {this.translate('signup_terms_accept_prefix', 'I have read and accept the')}{' '}
            <button className={classes.termsLink} type="button" onClick={this.openSignupModal}>
              {this.translate('signup_terms_and_conditions', 'terms and conditions')}
            </button>{this.translate('signup_terms_accept_suffix', '.')}
          </Typography>
        </div>
        {errors.termsandconditions && <FormHelperText error>{errors.termsandconditions}</FormHelperText>}
      </div>
    )
  }

  renderVerificationStep = () => {
    const { classes } = this.props
    const { info, verificationCodeSent } = this.state

    return (
      <div className={classes.verificationStep}>
        <Typography className={classes.verificationCopy}>
          {this.translate('signup_verification_copy', 'To protect your account, verify the email address entered during sign-up.')}
        </Typography>
        <div className={classes.verificationContacts}>
          <div>
            <Typography className={classes.verificationLabel}>{this.translate('signup_verification_email', 'Email address')}</Typography>
            <Typography className={classes.verificationValue}>{info.emailaddress}</Typography>
          </div>
          <div>
            <Typography className={classes.verificationLabel}>{this.translate('signup_contactnumber', 'Contact number')}</Typography>
            <Typography className={classes.verificationValue}>{info.contactnumber}</Typography>
          </div>
        </div>
        <div className={classes.verificationAction}>
          <div>
            <Typography className={classes.verificationActionTitle}>{this.translate('signup_verification_send_title', 'Send verification code')}</Typography>
            <Typography className={classes.verificationActionDescription}>{this.translate('signup_verification_send_description', 'A one-time code will be sent to your email address.')}</Typography>
          </div>
          <Button color="primary" onClick={this.sendVerificationCode} type="button">
            {verificationCodeSent ? this.translate('signup_send_again', 'Send again') : this.translate('signup_send_code', 'Send code')}
          </Button>
        </div>
        {verificationCodeSent && (
          <div className={classes.codeSentNotice}>{this.translate('signup_verification_code_sent', 'Verification code sent. Enter it below to continue.')}</div>
        )}
        {verificationCodeSent && (
          <div className={classes.codeInput}>
            {this.renderInput('verificationcode', this.translate('signup_verification_code_label', '6-digit verification code'), {
              helperText: this.translate('signup_verification_code_help', 'Use the code sent to your email address or contact number.'),
              inputProps: { inputMode: 'numeric', maxLength: 6, pattern: '[0-9]*' },
              required: false,
              type: 'tel'
            })}
          </div>
        )}
        {this.state.verificationError && <FormHelperText error>{this.state.verificationError}</FormHelperText>}
      </div>
    )
  }

  renderCurrentStep = () => {
    switch (this.state.step) {
      case 0: return this.renderAccountStep()
      case 1: return this.renderOrganisationStep()
      case 2: return this.renderWorkStep()
      case 3: return this.renderReviewStep()
      default: return this.renderVerificationStep()
    }
  }

  renderSuccess = () => {
    const { classes } = this.props

    return (
      <div className={classes.successState}>
        <CircleCheck aria-hidden="true" className={classes.successIcon} size={52} strokeWidth={1.8} />
        <Typography className={classes.successTitle} component="h2">{this.translate('signup_success_title', 'Your account is ready')}</Typography>
        <Typography className={classes.successCopy}>
          {this.translate('signup_success_copy', 'Your account and organisation preferences have been saved. You can now sign in to AnyWARE.')}
        </Typography>
        <div className={classes.successActions}>
          <Button color="primary" raised onClick={this.startAgain}>{this.translate('signup_start_again', 'Start again')}</Button>
          <Link className={classes.returnLink} to="/login">{this.translate('signup_return_to_sign_in', 'Return to sign in')}</Link>
        </div>
      </div>
    )
  }

  render() {
    const { activeLanguage, classes, translate } = this.props
    const { open, step, submitted } = this.state
    const steps = STEPS.map(wizardStep => ({
      ...wizardStep,
      title: this.translate(wizardStep.titleKey, wizardStep.title),
      description: this.translate(wizardStep.descriptionKey, wizardStep.description)
    }))
    const currentStep = steps[step]

    return (
      <div className={classes.root}>
        <form className={classes.formWrapper} noValidate onSubmit={this.handleSubmit}>
          <Grid container className={classes.signupPage}>
            <Grid item xs={12} md={4} className={classes.brandPanel}>
              <div className={classes.brandContent}>
                <img alt="TREND Networks" className={classes.logo} src="/images/login_logo.svg" />
                <Typography className={classes.brandTitle} component="h1">{this.translate('signup_brand_title', 'Set up your AnyWARE account')}</Typography>
                <Typography className={classes.brandCopy}>
                  {this.translate('signup_brand_copy', 'Tell us about your organisation so AnyWARE can start with the right defaults for your team.')}
                </Typography>
                <div className={classes.sidebarSteps}>
                  {steps.map((wizardStep, index) => (
                    <div className={classes.sidebarStep} key={wizardStep.title}>
                      <span className={`${classes.stepNumber} ${index === step ? classes.stepNumberCurrent : ''} ${index < step ? classes.stepNumberComplete : ''}`}>
                        {index < step ? <CircleCheck aria-hidden="true" size={17} /> : index + 1}
                      </span>
                      <div>
                        <Typography className={classes.sidebarStepTitle}>{wizardStep.title}</Typography>
                        <Typography className={classes.sidebarStepDescription}>{wizardStep.description}</Typography>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </Grid>
            <Grid item xs={12} md={8} className={classes.contentPanel}>
              <div className={classes.wizardContent}>
                {!submitted && (
                  <div className={classes.mobileProgress}>
                    {steps.map((wizardStep, index) => (
                      <span className={`${classes.mobileProgressDot} ${index <= step ? classes.mobileProgressDotActive : ''}`} key={wizardStep.title} />
                    ))}
                  </div>
                )}
                {submitted ? this.renderSuccess() : (
                  <React.Fragment>
                    <div className={classes.pageHeading}>
                      <Typography className={classes.stepCaption}>{this.translate('signup_step_caption', { current: step + 1, total: STEPS.length })}</Typography>
                      <Typography className={classes.headline} component="h2">{currentStep.title}</Typography>
                      <Typography className={classes.subheading}>{currentStep.description}</Typography>
                      {step === 0 && (
                        <Typography className={classes.signInPrompt}>
                          {this.translate('signup_existing_account_prompt', 'Already have an account?')} <Link className={classes.orangeLink} to="/login">{this.translate('signin', 'Sign in')}</Link>
                        </Typography>
                      )}
                    </div>
                    <div className={classes.stepContent}>{this.renderCurrentStep()}</div>
                    <div className={classes.actionRow}>
                      {step > 0 ? (
                        <Button className={classes.backButton} onClick={this.goToPreviousStep} type="button">
                          <ArrowLeft aria-hidden="true" size={17} /> {this.translate('organisation_preferences_back', 'Back')}
                        </Button>
                      ) : <span />}
                      <Button color="primary" disabled={this.state.submitting || this.state.verifyingOtp || (step === STEPS.length - 1 && !this.state.verificationCodeSent)} raised type={step === STEPS.length - 1 ? 'submit' : 'button'} onClick={step === STEPS.length - 1 ? undefined : this.goToNextStep}>
                        {step === STEPS.length - 1
                          ? (this.state.otpVerified
                            ? this.translate('signup_create_account', 'Create account')
                            : this.translate('signup_verify_and_create_account', 'Verify and create account'))
                          : <React.Fragment>{this.translate('general_continue', 'Continue')} <ArrowRight aria-hidden="true" size={17} /></React.Fragment>}
                      </Button>
                    </div>
                  </React.Fragment>
                )}
              </div>
            </Grid>
          </Grid>
        </form>
        {open && (
          <TermsAndCondition
            activeLanguage={activeLanguage}
            classes={classes}
            closeSignupModal={this.closeSignupModal}
            open={open}
            translate={translate}
          />
        )}
      </div>
    )
  }
}

export default withStyles(styles)(SignupForm)
